import { LogManager } from '@src/utility/Logger';

import type { Client } from '@datocms/cma-client-browser';
import type ApiClient from '@src/apiClient/ApiClient';

const logger = LogManager.getLogger('monitoring:aiPromptLogSaver');

export type AIPromptLogEntry = {
  prompt_title: string;
  prompt_type: string;
  /** DatoCMS ai_prompt record ID (link field) */
  aiPromptId?: string;
  /** DatoCMS ai_model record ID (link field) */
  modelRecordId?: string;
  result_status: 'success' | 'error';
  first_content_latency_ms?: number;
  latency_ms?: number;
  error_message?: string;
  personId?: string;
  storyId?: string;
  showcaseId?: string;
  metadata?: Record<string, unknown>;
};

/**
 * Fire-and-forget persistence of AI prompt log records to DatoCMS.
 * Mirrors the saveAiFlowLogs pattern but for single-call prompt records.
 */
export class AIPromptLogSaver {
  static async save(
    datoClient: Client | ApiClient,
    entry: AIPromptLogEntry,
  ): Promise<void> {
    try {
      const itemType = await datoClient.itemTypes.find('ai_prompt_log');

      const payload = {
        item_type: { type: 'item_type', id: itemType.id as string },
        prompt_title: entry.prompt_title,
        prompt_type: entry.prompt_type,
        ...(entry.aiPromptId && { ai_prompt: entry.aiPromptId }),
        ...(entry.modelRecordId && { model: entry.modelRecordId }),
        ...(entry.result_status && { result_status: entry.result_status }),
        ...(entry.first_content_latency_ms != null && { first_content_latency_ms: entry.first_content_latency_ms }),
        ...(entry.latency_ms != null && { latency_ms: entry.latency_ms }),
        ...(entry.error_message && { error_message: entry.error_message }),
        ...(entry.personId && { person: entry.personId }),
        ...(entry.storyId && { story: entry.storyId }),
        ...(entry.showcaseId && { showcase: entry.showcaseId }),
        ...(entry.metadata && {
          metadata: JSON.stringify(entry.metadata),
        }),
      };
      logger.debug(`payload: ${JSON.stringify(payload)}`);
      await datoClient.items.create(payload as any);

      logger.info(
        `saved ai_prompt_log: ${entry.prompt_type} "${entry.prompt_title}" status=${entry.result_status}`,
      );
    } catch (err: any) {
      logger.error(
        `failed to save ai_prompt_log: ${entry.prompt_type} "${entry.prompt_title}" — ${err?.message || err}`,
        err?.body || err?.response?.data || err,
      );
    }
  }
}
